<template>
	<TnCard
		class="accessory-product-card"
		shadow
		border-radius="l"
		@click="showModal(product, currentVariant)"
	>
		<TnBadge
			class="badge"
			size="s"
			responsive
			v-if="product.productLabel"
		>
			{{ product.productLabel }}
		</TnBadge>
		<!-- Grid row 1 -->
		<div
			class="image-container"
			:key="currentVariant"
		>
			<div class="img-wrapper">
				<template v-if="productVariants.length === 0 && product.productImage?.image_md">
					<NuxtImg
						:src="product.productImage.image_md.src"
						:modifiers="{ trim: 10 }"
						width="auto"
						height="140px"
					/>
				</template>
				<template v-else-if="displayedVariants.length > 0">
					<NuxtImg
						v-for="variant in displayedVariants"
						:alt="variant.image.alt"
						:src="variant.imageLink"
						v-show="variant.id === currentVariant"
						:key="variant.id"
						:modifiers="{ trim: 10 }"
						width="auto"
						height="140px"
					/>
				</template>
			</div>
		</div>

		<!-- Grid row 2 -->
		<div class="product-info">
			<div class="product-info-top">
				<!-- product-info-top-row-1 -->
				<div class="top-row-1">
					<TnParagraph
						responsive
						size="s"
						class="brand"
					>
						{{ product.brand }}
					</TnParagraph>
					<div class="variants">
						<ul class="variant-selector">
							<li
								class="variant"
								v-for="variant in displayedVariants"
								:class="{ active: currentVariant === variant.id }"
								@mouseover="changeVariant(variant)"
								:style="`background-color: ${variant.colorCode || variant.colorcode}`"
								:title="variant.color"
								:key="variant.id"
							></li>
							<li
								v-show="productVariants.length > 3"
								class="variant more"
							>
								+{{ productVariants.length - 3 }}
							</li>
						</ul>
					</div>
				</div>

				<!-- product-info-top-row-2 -->
				<TnParagraph
					responsive
					bold
					size="s"
					class="title top-row-2"
				>
					{{ productNameWithoutBrand }}
				</TnParagraph>
			</div>
			<div class="product-info-bottom">
				<div class="price">
					<TnParagraph
						responsive
						bold
						size="xl"
						class="price-top"
					>
						{{ formattedSalePrice ? formattedSalePrice : formattedPrice }}
					</TnParagraph>
					<TnParagraph
						v-if="formattedSalePrice"
						responsive
						size="xs"
						class="price-bottom"
						:class="{ 'strike-through': formattedSalePrice }"
					>
						Før {{ formattedPrice }}
					</TnParagraph>
				</div>
			</div>
		</div>

		<!-- Grid row 3 -->
		<div class="add">
			<TnButton
				v-if="isMobile && productVariants.length > 0"
				class="add-to-cart"
				size="xs"
				variant="primary"
			>
				Legg til
			</TnButton>
			<TnButton
				v-else
				class="add-to-cart"
				size="xs"
				variant="primary"
				@click.stop="addToCart(rawProduct)"
			>
				Legg til
			</TnButton>
		</div>
	</TnCard>
</template>
<script setup lang="ts">
import ResponsiveImageViewModel from "~/helpers/ViewModels/ResponsiveImageViewModel";
import { useProductNameFormatter, useAddToCartFormatter } from "./ECommerceProductFormatters";
import type { RawProduct, Product, Variant } from "./types";

defineComponent({
	name: "ECommerceProductAccessoryCard",
});

const props = defineProps<{
	product: Product;
	rawProduct: RawProduct;
}>();

// Emits
const emit = defineEmits(["add-to-cart", "show-modal"]);

// Methods
const addToCart = (rawProduct: RawProduct) => {
	if (productVariants.value.length > 0) {
		const variant = productVariants.value.find((variant) => variant.id === currentVariant.value);
		emit("add-to-cart", useAddToCartFormatter(variant));
		return;
	}
	emit("add-to-cart", useAddToCartFormatter(rawProduct));
};

const showModal = (product, currentVariant) => {
	emit("show-modal", product, currentVariant);
};

// COMPUTED
const { productNameWithoutBrand } = useProductNameFormatter(props.product);

const formattedPrice = computed(() => {
	if (productVariants.value.length > 0) {
		const price = productVariants.value.find((variant) => variant.id === currentVariant.value)?.priceWithVat;
		return `${price},-`;
	}
	const price = props.rawProduct.priceWithVat;
	return `${price},-`;
});

const formattedSalePrice = computed(() => {
	if (productVariants.value.length > 0) {
		const price = productVariants.value.find((variant) => variant.id === currentVariant.value)?.salePriceWithVat;
		return price ? `${price},-` : undefined;
	}
	const price = props.rawProduct?.salePriceWithVat;
	return price ? `${price},-` : undefined;
});

const displayedVariants = computed(() => {
	if (productVariants.value.length > 0) {
		return productVariants.value.slice(0, 3);
	}
	return [];
});

const productVariants = computed(() => {
	if (props.product.variants?.length > 1) {
		const variants = props.product.variants.map((variant) => {
			return {
				...variant,
				image: variant.imageLink && ResponsiveImageViewModel(variant.imageLink, undefined, "280px"),
			};
		});

		const sortedVariants = [
			variants.find((v) => v.id === props.product.productId),
			...variants.filter((v) => v.id !== props.product.productId),
		]
			.filter((v) => v)
			.sort((a, b) => b.inventory?.[0]?.amount - a.inventory?.[0]?.amount);

		let uniqueVariants = [];
		for (const item of sortedVariants) {
			const isDuplicate = uniqueVariants.find((obj) => obj.colorCode === item.colorCode);
			if (!isDuplicate) {
				uniqueVariants.push(item);
			}
		}

		uniqueVariants = uniqueVariants.sort((a, b) => a?.priority - b?.priority);

		return uniqueVariants;
	} else return [];
});
const changeVariant = (variant: Variant) => {
	currentVariant.value = variant.id;
};

// Reactive variables
const currentVariant = ref<string | null>(displayedVariants?.value?.[0]?.id || props.product.productId);
const { isMobile } = useIsMobile();
</script>
<style lang="scss" scoped>
.accessory-product-card {
	display: grid;
	padding: $spacing-3xl $spacing-l $spacing-xl $spacing-l;
	grid-template-rows: 150px calc(140px + ($spacing-l * 2)) 32px;
	width: 234.5px;
	min-width: 234.5px;
	margin-top: $spacing-xs;
	margin-bottom: $spacing-l;
	@include breakpoint(mobile) {
		padding: $spacing-xl $spacing-m $spacing-m $spacing-m;
		grid-template-rows: 100px 100px 32px;
		width: 150px;
		min-width: 150px;
	}
	& {
		* {
			max-width: 100%;
			max-height: 100%;
		}
	}
}
.badge {
	position: absolute;
	top: 0;
	left: $spacing-l;
	@include breakpoint(mobile) {
		left: $spacing-m;
	}
}
.image-container {
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	cursor: pointer;
	@include breakpoint(mobile) {
		padding: $spacing-s;
	}
	.img-wrapper {
		display: flex;
		& {
			img {
				object-fit: contain;
			}
		}
	}
}
.product-info {
	display: flex;
	flex-direction: column;
	gap: $spacing-m;
	margin: $spacing-l 0;
	@include breakpoint(mobile) {
		gap: 0;
		margin: 0;
	}
	& {
		.product-info-top {
			display: flex;
			flex-direction: column;
			& {
				.top-row-1 {
					display: flex;
					justify-content: space-between;
				}
			}
		}
	}
	& {
		* {
			display: flex;
		}
	}
}
.brand {
	text-align: start;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	line-clamp: 1;
	box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}
.title {
	text-align: start;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	line-clamp: 2;
	box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	min-height: 44px;
	@include breakpoint(mobile) {
		min-height: 32px;
	}
}
.variants {
	transform: translateY(-2px);
}
.add {
	display: flex;
}
.price {
	display: flex;
	flex-direction: column;
	justify-content: center;
	& {
		&-top {
			color: $color-primary-mid;
		}
	}
}
.variant-selector {
	display: flex;
	gap: 2px;
	transform: translate(3px, 2px);

	.variant {
		cursor: pointer;
		width: 16px;
		height: 16px;
		display: flex;
		border-radius: 50%;
		border: 2px solid white;
		box-shadow: 0 2px 3px 0 #0003 inset;
		transition: transform 0.3s ease;

		&.active:not(.more) {
			outline: 2px solid $color-cta-active;
			@include breakpoint(mobile) {
				outline: 1px solid $color-cta-active;
			}
		}

		&.more {
			width: auto;
			border-radius: 0;
			box-shadow: none;
			position: relative;
			top: -1px;

			@include font-text-s;
		}

		@include breakpoint(mobile) {
			width: 14px;
			height: 14px;
		}
	}
}

.variants {
	z-index: 2000;
	display: flex;
	align-items: baseline;
	align-self: center;
	justify-content: flex-end;
	min-height: 100%;

	@include breakpoint(mobile) {
		height: 16px;
	}

	@include breakpoint(mobile) {
		justify-content: flex-start;
	}
}
.strike-through {
	text-decoration: line-through;
	margin-right: $spacing-s;
}
</style>
